import "../../styles/capture.css";
import type { JSX } from "preact";
import type { Card, CardKind } from "../../../../shared/types.ts";
import { domainInitial, FILE_FACE } from "../lib.ts";
import { store } from "../state.ts";

// Dono: gap-capture. PDF, vídeo e áudio (spec §4.3, §7.11) no detalhe e na grade, e o player de links do YouTube e do
// Vimeo (domínios sem cookies, §4.3 Vídeo). Montado pelo Detail.tsx (DetailMedia) e pelo Grid.tsx (Frame).

const S = (d: string, w = 1.75) => () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width={w} stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const IconDoc = S("M6 3h8.5L19 7.5V21H6zM14 3v5h5M9 12.5h7M9 16h7M9 9h3");
const IconPlay = S("M8 5.5v13l11-6.5z", 2);
const IconWave = S("M4 10v4M8 6.5v11M12 9v6M16 4.5v15M20 10v4", 2);

const FILE_KINDS = new Set<CardKind>(["pdf", "video", "audio", "file"]);
const KIND_NAME: Partial<Record<CardKind, string>> = { pdf: "PDF", video: "Vídeo", audio: "Áudio", file: "Arquivo" };
export const FACE_ICON: Partial<Record<CardKind, () => JSX.Element>> = { pdf: IconDoc, video: IconPlay, audio: IconWave, file: IconDoc };

export const isFileKind = (k: CardKind) => FILE_KINDS.has(k);
/**
 * O tipo do menu é um rótulo: link ou nota que a pessoa marcou como Vídeo, PDF, Áudio ou Arquivo continua mostrando o
 * que guarda (o link, com o player se for YouTube/Vimeo, ou o texto). Só card com o arquivo vira player ou face de arquivo.
 */
export const shownAs = (c: Card): Card =>
  isFileKind(c.kind) && !c.primaryBlobId && !store.previews.value.get(c.id) ? { ...c, kind: c.content || !c.sourceUrl ? "note" : "link" } : c;
/** O detalhe usa Media no lugar do caminho de sempre: arquivo (PDF, vídeo, áudio) ou link do YouTube/Vimeo. Recebe shownAs(c). */
export const hasMedia = (c: Card) => isFileKind(c.kind) || (c.kind === "link" && embedUrl(c.sourceUrl) !== null);

/** Arquivo do card: no servidor (/media, §7.9) ou, enquanto sobe, a cópia local (object URL). */
const fileSrc = (c: Card) => (c.primaryBlobId ? `/media/${encodeURIComponent(c.primaryBlobId)}` : (store.previews.value.get(c.id) ?? null));

/** "90", "1m30s", "1h2m3s" → segundos. */
const seconds = (t: string | null) => {
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(t ?? "");
  return m ? Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0) : 0;
};

/**
 * Player embutido de um link salvo: youtube-nocookie.com ou player.vimeo.com (com dnt=1). null = não é vídeo desses
 * dois. O endereço é montado aqui com o id validado; nada do link salvo vai para o iframe além do id e do tempo.
 */
export function embedUrl(raw: string | null): string | null {
  let u: URL;
  try {
    u = new URL(raw ?? "");
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.replace(/^(www|m|music)\./, "");
  const yt =
    host === "youtu.be" ? u.pathname.slice(1).split("/")[0]
    : host === "youtube.com" || host === "youtube-nocookie.com" ? (u.searchParams.get("v") ?? /^\/(?:embed|shorts|live|v)\/([^/?#]+)/.exec(u.pathname)?.[1])
    : null;
  if (yt && /^[\w-]{11}$/.test(yt)) {
    const start = seconds(u.searchParams.get("t") ?? u.searchParams.get("start"));
    // playsinline=1: no iPhone o player toca dentro da página, não pula para a tela cheia.
    return `https://www.youtube-nocookie.com/embed/${yt}?playsinline=1${start ? `&start=${start}` : ""}`;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // vimeo.com/123456, /channels/x/123456, /123456/hash (não listado), player.vimeo.com/video/123456?h=hash
    const m = /\/(\d{5,12})(?:\/([0-9a-f]{6,20}))?\/?$/.exec(u.pathname);
    const h = m?.[2] ?? u.searchParams.get("h");
    if (m) return `https://player.vimeo.com/video/${m[1]}?dnt=1${h && /^[0-9a-f]{6,20}$/.test(h) ? `&h=${h}` : ""}`;
  }
  return null;
}

/** Face na grade: desenho próprio por tipo; o PDF mostra o começo do texto (ou do resumo) quando a ingestão já leu. */
export function FileFace({ card: c }: { card: Card }) {
  const Icon = FACE_ICON[c.kind] ?? IconDoc;
  const text = c.kind === "pdf" ? (c.summary ?? c.contentText)?.slice(0, 320) : null;
  return (
    <div class={`frame fileface is-${c.kind}`} style={`aspect-ratio:${1 / (FILE_FACE[c.kind] ?? 1)}`}>
      {text ? (
        <p class="fileface-text">{text}</p>
      ) : (
        <span class="fileface-icon" aria-hidden="true">
          <Icon />
        </span>
      )}
      <span class="fileface-badge" aria-hidden="true">
        {KIND_NAME[c.kind]}
      </span>
    </div>
  );
}

/**
 * Celular: PDF em iframe não funciona (o iPhone desenha só a primeira página, o Chrome do Android nada). Lá o PDF abre no
 * leitor do próprio aparelho (Abrir PDF). Avaliado a cada render: girar o tablet troca o modo.
 */
const phone = () => matchMedia("(pointer: coarse), (max-width: 768px)").matches;
/**
 * App instalado no iPhone (Tela de Início): target=_blank abriria o PDF num navegador à parte, sem a sessão (o /media
 * pede login). Lá ele abre na própria janela do app, e o gesto de voltar (borda esquerda) traz de volta.
 */
const standalone = () => (navigator as { standalone?: boolean }).standalone === true;

export function Media({ card: c, link }: { card: Card; link: string | null }) {
  if (c.kind === "link") return <Embed card={c} link={link} />;
  const src = fileSrc(c)!; // shownAs: card de arquivo aqui sempre tem o arquivo
  const name = c.title || KIND_NAME[c.kind] || "Arquivo";
  const Icon = FACE_ICON[c.kind] ?? IconDoc;
  // Arquivo ainda subindo (prévia blob:): um iframe dela é barrado pela CSP (frame-src) e não há o que abrir fora daqui.
  const sending = !c.primaryBlobId;
  const pdfCard = c.kind === "pdf" && (sending || phone());
  let player: JSX.Element;
  // #t=0.001: o Safari do iPhone só pinta o primeiro quadro (no lugar de uma caixa preta) quando o vídeo começa nele.
  if (c.kind === "video") player = <video class="media-video" src={`${src}#t=0.001`} controls playsInline preload="metadata" aria-label={`Vídeo: ${name}`} />;
  // Sem o atributo sandbox: o Chromium não desenha PDF em iframe com sandbox. O documento já chega isolado pelo
  // cabeçalho do /media (Content-Security-Policy: sandbox; default-src 'none').
  else if (c.kind === "pdf" && !pdfCard) player = <iframe class="media-pdf" src={src} title={`PDF: ${name}`} loading="lazy" />;
  else {
    player = (
      <div class="media-card">
        <span class="media-card-icon" aria-hidden="true">
          <Icon />
        </span>
        <h2>{name}</h2>
        {c.kind === "audio" && <audio src={src} controls preload="metadata" aria-label={`Áudio: ${name}`} />}
        {pdfCard &&
          (sending ? (
            <p class="media-card-state" role="status">
              Enviando…
            </p>
          ) : (
            <a class="btn primary media-open" href={src} target={standalone() ? undefined : "_blank"} rel="noopener">
              Abrir PDF
            </a>
          ))}
      </div>
    );
  }
  return (
    <div class={`media-file is-${c.kind}`}>
      {player}
      <p class="media-actions">
        {c.kind === "pdf" && !pdfCard && (
          <a href={src} target="_blank" rel="noopener">
            Abrir em outra aba
          </a>
        )}
        <a href={src} download={c.title ?? ""}>
          Baixar {KIND_NAME[c.kind]?.toLowerCase() ?? "arquivo"}
        </a>
      </p>
    </div>
  );
}

function Embed({ card: c, link }: { card: Card; link: string | null }) {
  const title = c.title ?? c.sourceUrl ?? "";
  return (
    <div class="link-view">
      <div class="media-embed">
        <iframe
          src={embedUrl(c.sourceUrl)!}
          title={`Vídeo: ${title}`}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerpolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
      <h2>{title}</h2>
      <p class="domain">
        <span class="favicon" aria-hidden="true">
          {domainInitial(c.sourceDomain)}
        </span>
        {c.sourceDomain}
      </p>
      {link && (
        <a class="open-site" href={link} target="_blank" rel="noopener noreferrer">
          Abrir no site<span class="sr-only"> (abre em outra aba)</span>
        </a>
      )}
    </div>
  );
}
